import { useState, useEffect, useRef } from "react";
import { Calendar, ChevronDown, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeekSelector({ selectedWeek, onWeekChange }) {
  const [weeks, setWeeks] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchWeeks = async () => {
      try {
        const response = await axios.get(`${API}/weeks/available`);
        setWeeks(response.data);
        
        // Set initial week if not already set
        if (!selectedWeek && response.data.length > 0) {
          onWeekChange(response.data[0]);
        }
      } catch (error) {
        console.error("Failed to fetch weeks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWeeks();
  }, [selectedWeek, onWeekChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleWeekSelect = (week) => {
    onWeekChange(week);
    setIsOpen(false);
  };

  if (loading || !selectedWeek) {
    return (
      <div className="flex justify-center py-3">
        <div className="inline-flex items-center gap-2 bg-white border-2 border-[#1aa59a] rounded-full px-4 py-2 shadow-sm">
          <div className="w-4 h-4 border-2 border-[#1aa59a] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium text-gray-500">Loading menu...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center py-3" ref={dropdownRef}>
      <div className="relative">
        {/* Selected Week Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center gap-2 bg-white border-2 border-[#1aa59a] rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-all cursor-pointer group"
          data-testid="week-selector-button"
        >
          <Calendar className="w-4 h-4 text-[#1aa59a]" />
          <span className="text-sm sm:text-base font-medium text-gray-800">
            {selectedWeek.display_label}
          </span>
          <ChevronDown 
            className={`w-4 h-4 text-[#1aa59a] transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-2">
              <p className="text-xs text-gray-500 px-3 py-2 font-medium">
                Select delivery week
              </p>
              
              {weeks.map((week) => (
                <button
                  key={week.offset}
                  onClick={() => handleWeekSelect(week)}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-lg transition-colors ${
                    selectedWeek.week_number === week.week_number && selectedWeek.offset === week.offset
                      ? 'bg-[#1aa59a]/10 text-[#1aa59a]'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                  data-testid={`week-option-${week.week_number}`}
                >
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">
                      Week {week.week_number} Menu
                    </span>
                    <span className="text-sm text-gray-500">
                      {week.week_start_display} - {week.week_end_display}
                    </span>
                  </div>
                  
                  {selectedWeek.week_number === week.week_number && selectedWeek.offset === week.offset && (
                    <Check className="w-5 h-5 text-[#1aa59a]" />
                  )}
                  
                  {week.offset === 0 && (
                    <span className="text-xs bg-[#1aa59a] text-white px-2 py-0.5 rounded-full ml-2">
                      Current
                    </span>
                  )}
                </button>
              ))}
            </div>
            
            <div className="border-t px-4 py-2 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Order up to 2 weeks in advance
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
